import axios from "axios";

function isLegacyCorsMode() {
	return `${process.env.REACT_APP_CORS_MODE || ""}`.trim().toLowerCase() === "legacy";
}

function shouldUseCredentialedCors() {
	return !isLegacyCorsMode();
}

const ACCESS_TOKEN_STORAGE_KEY = "agic_access_token";
const REFRESH_TOKEN_STORAGE_KEY = "agic_refresh_token";

function canUseLocalStorage() {
	return typeof window !== "undefined" && !!window.localStorage;
}

export function storeAccessToken(token) {
	if (!canUseLocalStorage()) return;

	if (token) {
		window.localStorage.setItem(ACCESS_TOKEN_STORAGE_KEY, token);
		return;
	}

	window.localStorage.removeItem(ACCESS_TOKEN_STORAGE_KEY);
}

export function storeRefreshToken(token) {
	if (!canUseLocalStorage()) return;

	if (token) {
		window.localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, token);
		return;
	}

	window.localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
}

function getStoredAccessToken() {
	if (!canUseLocalStorage()) return "";
	return window.localStorage.getItem(ACCESS_TOKEN_STORAGE_KEY) || "";
}

export function getStoredRefreshToken() {
	if (!canUseLocalStorage()) return "";
	return window.localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY) || "";
}

/** This is so we can specify the baseurl and other common headers for each query immidiately */
export const tomCat = axios.create({
	baseURL: process.env.REACT_APP_BACKEND_URL,
	headers: {
		Accept: "application/json;*/*",
		"Content-Type": "application/json",
	},
});

export const dNet = axios.create({
	baseURL:process.env.REACT_APP_DOT_NET_BACKEND_URL,
	headers: {
		Accept: "application/json;*/*",
		"Content-Type": "application/json",
		
	},
});

export const nodeApi = axios.create({
	baseURL: process.env.REACT_APP_API_URL,
	withCredentials: shouldUseCredentialedCors(),
	headers: {
		Accept: "application/json;*/*",
		"Content-Type": "application/json",
	},
});

function isAuthSessionRequest(config) {
	const url = config?.url || "";
	return (
		config?.skipAuthRefresh === true ||
		/\/auth\/refresh(?:\?.*)?$/.test(url) ||
		/\/auth\/logout(?:\?.*)?$/.test(url)
	);
}

// When JWT auth is on, attach Bearer token from Redux to every request.
// Store is read lazily to avoid circular dependency (globals/index -> reducers -> modules that use nodeApi).
function getStore() {
	try {
		return require("globals/index").default;
	} catch {
		return null;
	}
}

nodeApi.interceptors.request.use((config) => {
	if (config?.skipAuthorization === true || isAuthSessionRequest(config)) {
		return config;
	}

	if (process.env.REACT_APP_JWT_AUTHENTICATION === "true" || process.env.REACT_APP_JWT_AUTHENTICATION === "1") {
		const store = getStore();
		const token = store?.getState?.().auth?.token || getStoredAccessToken();
		if (token) {
			config.headers = config.headers ?? {};
			config.headers.Authorization = `Bearer ${token}`;
		}
	}
	return config;
});

// On 401, try refresh once and retry (JWT auth only)
nodeApi.interceptors.response.use(
	(res) => res,
	async (err) => {
		const config = err.config;
		const jwtAuth = process.env.REACT_APP_JWT_AUTHENTICATION === "true" || process.env.REACT_APP_JWT_AUTHENTICATION === "1";
		if (!config || !jwtAuth || err.response?.status !== 401 || config._retryAfterRefresh || isAuthSessionRequest(config)) {
			return Promise.reject(err);
		}
		config._retryAfterRefresh = true;
		const { refreshAccessToken } = await import("helpers/auth");
		const { success } = await refreshAccessToken();
		if (success) {
			return nodeApi.request(config);
		}
		return Promise.reject(err);
	}
);

// export const googleApi = axios.create({
// 	baseURL: process.env.REACT_APP_GOOGLE_API_URL,
// 	headers: {
// 		Accept: "application/json;*/*",
// 		"Access-Control-Allow-Origin": "*",
// 		"Content-Type": "application/json",
// 	},
// });
